import React, { useEffect, useRef, useState } from 'react';
import { HiOutlineMenuAlt3 } from 'react-icons/hi';
import { IoMdClose, IoIosArrowForward } from 'react-icons/io';
import { BsStars } from 'react-icons/bs';
import { GiCargoCrane } from 'react-icons/gi';
import { TbCrane } from 'react-icons/tb';
import { FaBasketShopping, FaIndustry } from 'react-icons/fa6';

import LocalizedLink from '../localizedLink';
import HeaderLink from '../NewHeader/HeaderLink';
import Icon from '../Icon';
import { LangSwitcher } from '../LangSwitcher/LangSwitcher';
import { isExternalUrl, shouldOpenCtaInNewTab } from '../../utils/utils';

import * as styles from './NavBar.module.scss';

const CircularEconomyIcon = ({ size = 24, className, ...props }) => (
  <img
    loading="lazy"
    decoding="async"
    src="/images/monstock-icone-final-economie-circulaire.svg"
    width={size}
    height={size}
    className={className}
    alt=""
    {...props}
  />
);

const LogisticsIcon = ({ size = 24, className, ...props }) => (
  <img
    loading="lazy"
    decoding="async"
    src="/images/logistique.svg"
    width={size}
    height={size}
    className={className}
    alt=""
    {...props}
  />
);

function NavBar({
  data,
  isMenuOpen,
  setIsMenuOpen,
  menuButtonRef,
  flags,
  locale,
}) {
  const navData = data || {};
  const [openDetailKey, setOpenDetailKey] = useState(null);
  const langSwitcherCloseRef = useRef(null);

  const handleMenuLinkClick = () => {
    setIsMenuOpen(false);
    setOpenDetailKey(null);
    langSwitcherCloseRef.current?.();
  };

  const handleLangSwitcherToggle = (nextOpen) => {
    if (nextOpen) {
      setOpenDetailKey(null);
      if (isMenuOpen) {
        setIsMenuOpen(false);
      }
    }
  };

  const handleRegisterLangSwitcherClose = (closeFn) => {
    langSwitcherCloseRef.current = closeFn;
  };

  const handleDetailMouseEnter = (detailKey) => () => {
    setOpenDetailKey(detailKey);
    langSwitcherCloseRef.current?.();
  };

  const handleDetailMouseLeave = (detailKey) => () =>
    setOpenDetailKey((current) => (current === detailKey ? null : current));

  const handleDetailToggle = (detailKey) => (event) => {
    const isOpen = event.currentTarget.open;
    setOpenDetailKey((current) => {
      if (isOpen) return detailKey;
      return current === detailKey ? null : current;
    });
    if (isOpen) {
      langSwitcherCloseRef.current?.();
    }
  };

  useEffect(() => {
    if (isMenuOpen) {
      langSwitcherCloseRef.current?.();
    }
  }, [isMenuOpen]);

  const iconsMap = {
    menu: HiOutlineMenuAlt3,
    close: IoMdClose,
    left: IoIosArrowForward,
    ia: BsStars,
    retail: FaBasketShopping,
    construction: TbCrane,
    intervention: GiCargoCrane,
    industry: FaIndustry,
    circular: CircularEconomyIcon,
    warehouse: LogisticsIcon,
  };

  const {
    logo_monstock,
    solution_group_name,
    needs_group_name,
    needs_group,
    sectors_group_name,
    sectors_group,
    links_group,
    body,
  } = navData;

  const safeBody = body ?? [];

  const solutionData = {
    solution_group_name,
    needs_group_name,
    needs_group,
    sectors_group_name,
    sectors_group,
    links_group,
  };

  const needsGroup = solutionData.needs_group ?? [];
  const sectorsGroup = solutionData.sectors_group ?? [];
  const linksGroup = solutionData.links_group ?? [];
  const ctaSlice = safeBody.find(
    (navlink) => navlink?.primary?.group_id === 'cta'
  );
  const navSlices = safeBody.filter(
    (navlink) => navlink?.primary?.group_id !== 'cta'
  );

  const renderSolutionLink = (key, linkData, label, className, content) => {
    const children = content ?? label;
    if (!children) return null;
    return (
      <li key={key}>
        <HeaderLink
          link={linkData}
          className={className}
          onClick={handleMenuLinkClick}
        >
          {children}
        </HeaderLink>
      </li>
    );
  };

  return (
    <nav className={styles.navbar} aria-label="Navigation principale">
      <div className={styles.navbar_container}>
        <LocalizedLink
          to="/"
          className={styles.navbar_logoLink}
          aria-label="Home"
        >
          <img
            className={styles.navbar_logo}
            src={logo_monstock?.url}
            alt={logo_monstock?.alt || 'Logo Monstock'}
            width={185}
            height={50}
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
        </LocalizedLink>

        <ul className={styles.navBar_rootList}>
          <li className={styles.navBar_rootItem}>
            <details
              className={styles.navBar_detailMain}
              open={openDetailKey === 'solutions'}
              onToggle={handleDetailToggle('solutions')}
              onMouseEnter={handleDetailMouseEnter('solutions')}
              onMouseLeave={handleDetailMouseLeave('solutions')}
            >
              <summary className={styles.navBar_title}>
                {solutionData.solution_group_name}
                <Icon
                  name="left"
                  iconsMap={iconsMap}
                  className={styles.navBar_icon}
                />
              </summary>
              <div className={styles.navBar_solutionsPanel}>
                <div className={styles.navBar_solutionsGrid}>
                  <div className={styles.navBar_solutionsColumn}>
                    <span className={styles.navBar_solutionsTitle}>
                      {solutionData.needs_group_name}
                    </span>
                    <ul className={styles.navBar_solutionsList}>
                      {needsGroup.map((need) => {
                        const content = (
                          <>
                            <img
                              loading="lazy"
                              decoding="async"
                              src="/images/icon_besoins.svg"
                              alt=""
                              aria-hidden="true"
                              className={styles.navBar_solutionsNeedIcon}
                            />
                            <span>{need.needs_link_name}</span>
                          </>
                        );

                        return renderSolutionLink(
                          need.needs_link_id,
                          need.needs_link,
                          need.needs_link_name,
                          styles.navBar_solutionsLink,
                          content
                        );
                      })}
                    </ul>
                  </div>
                  <div className={styles.navBar_solutionsColumn}>
                    <span className={styles.navBar_solutionsTitle}>
                      {solutionData.sectors_group_name}
                    </span>
                    <ul className={styles.navBar_solutionsPillList}>
                      {sectorsGroup.map((sector) => {
                        const iconName = sector.sectors_link_id;
                        const content = (
                          <>
                            {iconName && (
                              <Icon
                                name={iconName}
                                iconsMap={iconsMap}
                                className={styles.navBar_solutionsPillIcon}
                                size={22}
                              />
                            )}
                            <span>{sector.sectors_link_name}</span>
                          </>
                        );

                        return renderSolutionLink(
                          sector.sectors_link_id,
                          sector.sectors_link,
                          sector.sectors_link_name,
                          styles.navBar_solutionsPill,
                          content
                        );
                      })}
                    </ul>
                  </div>
                  <div className={styles.navBar_solutionsColumnRight}>
                    <ul className={styles.navBar_solutionsRightList}>
                      {linksGroup.map((link) =>
                        renderSolutionLink(
                          link.group_link_id,
                          link.group_link,
                          link.group_link_name,
                          styles.navBar_solutionsRightLink
                        )
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </details>
          </li>
          {navSlices.map((navlink, index) => {
            const primary = navlink?.primary ?? {};
            const baseKey =
              primary.group_id || primary.group_name || `slice-${index}`;
            if (
              primary.link_name?.url ||
              primary.link_name?.uid ||
              !navlink.items?.length
            ) {
              const itemKey =
                primary.link_name?.uid ||
                primary.group_id ||
                primary.group_name ||
                `slice-${index}`;
              return (
                <li key={itemKey}>
                  <HeaderLink
                    link={primary.link_name}
                    className={styles.navBar_title}
                    onClick={handleMenuLinkClick}
                  >
                    {primary.group_name}
                  </HeaderLink>
                </li>
              );
            }

            if (primary.group_id === 'ia') {
              const items = navlink.items ?? [];
              const detailKey = `detail-${baseKey}`;
              return (
                <li key={baseKey}>
                  <details
                    className={`${styles.navBar_detailMain} ${styles.navBar_detailMainIa}`}
                    open={openDetailKey === detailKey}
                    onToggle={handleDetailToggle(detailKey)}
                    onMouseEnter={handleDetailMouseEnter(detailKey)}
                    onMouseLeave={handleDetailMouseLeave(detailKey)}
                  >
                    <summary className={styles.navBar_title}>
                      <span className={styles.navBar_titleIa}>
                        <Icon
                          name="ia"
                          iconsMap={iconsMap}
                          className={styles.navBar_iconIa}
                        />
                        {primary.group_name}
                      </span>
                      <Icon
                        name="left"
                        iconsMap={iconsMap}
                        className={styles.navBar_icon}
                      />
                    </summary>
                    <ul className={styles.navBar_detailList}>
                      {items.map((link) => {
                        return (
                          <li key={link.link_id}>
                            <HeaderLink
                              link={link.navigation_link}
                              className={styles.navBar_detailLink}
                              onClick={handleMenuLinkClick}
                            >
                              {link.link_name}
                            </HeaderLink>
                          </li>
                        );
                      })}
                    </ul>
                  </details>
                </li>
              );
            }

            const items = navlink.items ?? [];
            const detailKey = `detail-${baseKey}`;
            return (
              <li key={baseKey}>
                <details
                  className={styles.navBar_detailMain}
                  open={openDetailKey === detailKey}
                  onToggle={handleDetailToggle(detailKey)}
                  onMouseEnter={handleDetailMouseEnter(detailKey)}
                  onMouseLeave={handleDetailMouseLeave(detailKey)}
                >
                  <summary className={styles.navBar_title}>
                    {primary.group_name}
                    <Icon
                      name="left"
                      iconsMap={iconsMap}
                      className={styles.navBar_icon}
                    />
                  </summary>
                  <ul className={styles.navBar_detailList}>
                    {items.map((link) =>
                      renderSolutionLink(
                        link.link_id,
                        link.navigation_link,
                        link.link_name,
                        styles.navBar_detailLink
                      )
                    )}
                  </ul>
                </details>
              </li>
            );
          })}
        </ul>
        {ctaSlice && (
          <ul className={`${styles.menuModalCtaList} ${styles.navBar_ctaList}`}>
            {(ctaSlice.items ?? []).map((button) => {
              const buttonUrl = button?.navigation_link?.url;
              const openInNewTab = shouldOpenCtaInNewTab(
                button.link_id,
                button.link_name
              );
              const isSecondary = button.link_id === 'connection';
              const buttonClassName = `${styles.menuModalCtaButton} ${
                isSecondary
                  ? styles.menuModalCtaButtonSecondary
                  : styles.menuModalCtaButtonPrimary
              }`;

              if (openInNewTab) {
                return (
                  <li key={button.link_id} className={styles.menuModalCtaItem}>
                    <a
                      className={buttonClassName}
                      href={buttonUrl}
                      rel={
                        isExternalUrl(buttonUrl)
                          ? 'nofollow noopener noreferrer'
                          : 'noopener noreferrer'
                      }
                      target="_blank"
                      onClick={handleMenuLinkClick}
                    >
                      {button.link_name}
                    </a>
                  </li>
                );
              }

              return (
                <li key={button.link_id} className={styles.menuModalCtaItem}>
                  <a
                    className={buttonClassName}
                    href={buttonUrl}
                    onClick={handleMenuLinkClick}
                  >
                    {button.link_name}
                  </a>
                </li>
              );
            })}
            <li
              className={`${styles.menuModalCtaItem} ${styles.navBar_langSwitcherItem}`}
            >
              <LangSwitcher
                flags={flags}
                localeInfo={locale}
                onRegisterClose={handleRegisterLangSwitcherClose}
                onToggle={handleLangSwitcherToggle}
              />
            </li>
          </ul>
        )}

        <button
          ref={menuButtonRef}
          className={styles.navbar_menu}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
          aria-label={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
        >
          <span
            className={`${styles.iconSwap} ${isMenuOpen ? styles.isOpen : ''}`}
          >
            <Icon name="menu" iconsMap={iconsMap} className={styles.iconMenu} />
            <Icon
              name="close"
              iconsMap={iconsMap}
              className={styles.iconClose}
            />
          </span>
        </button>
      </div>
    </nav>
  );
}

export default NavBar;
